import React, { useState } from "react";
import { Avatar, Box, Button, Card, Chip, CircularProgress, FormControlLabel, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Switch, Tooltip, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useNavigate } from "react-router-dom";
import { hasConfig, missingRequirements, pluginIcon } from "./pluginHelpers";

// Tarjeta de plugin, en el mismo idioma que la de locales: ícono, nombre con su categoría, descripción y abajo la acción
// principal. Instalado: interruptor para activarlo, configuración y, en el menú ⋮ (no es de todos los días),
// desinstalar. No instalado: «Instalar», que lo deja activo, o «Solicitar» si la empresa no lo tiene contratado
// (superadmin lo habilita desde su panel).
const PluginCard = ({ plugin, busy = false, isSuperadmin = false, onInstall, onUninstall, onToggleActive, onConfigure, onRequest }) => {
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const Icon = pluginIcon(plugin);
    const missing = missingRequirements(plugin);
    const configurable = hasConfig(plugin);
    const needsConfig = plugin.installed && plugin.missing_required_fields?.length > 0;
    const active = plugin.installed && plugin.enabled;

    const runAction = (action) => () => {
        setMenuAnchor(null);
        action(plugin);
    };

    const footer = () => {
        if (plugin.installed) {
            return (
                <>
                    <FormControlLabel
                        sx={{ mr: 0 }}
                        disabled={busy}
                        control={<Switch size="small" color="success" checked={!!plugin.enabled} onChange={() => onToggleActive(plugin)} />}
                        label={<Typography variant="body2">{plugin.enabled ? "Activo" : "Inactivo"}</Typography>}
                    />
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {busy && <CircularProgress size={16} />}
                        {needsConfig ? (
                            <Button size="small" color="warning" variant="outlined" disabled={busy} startIcon={<SettingsOutlinedIcon />} onClick={() => onConfigure(plugin)}>
                                Falta configurar
                            </Button>
                        ) : configurable && (
                            <Button size="small" disabled={busy} startIcon={<SettingsOutlinedIcon />} onClick={() => onConfigure(plugin)}>
                                Configurar
                            </Button>
                        )}
                    </Stack>
                </>
            );
        }
        if (!plugin.available) {
            return (
                <>
                    <Chip size="small" variant="outlined" icon={<LockOutlinedIcon />} label="No contratado" />
                    {isSuperadmin
                        ? <Button size="small" onClick={() => navigate("/superadmin/plugins")}>Habilitar</Button>
                        : <Button size="small" variant="outlined" onClick={() => onRequest(plugin)}>Solicitar</Button>}
                </>
            );
        }
        return (
            <>
                <span />
                <Button size="small" variant="contained" disabled={busy || !!missing.length} onClick={() => onInstall(plugin)}
                        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}>
                    Instalar
                </Button>
            </>
        );
    };

    return (
        <Card
            variant="outlined"
            sx={{
                height: "100%",
                p: 2,
                borderRadius: 2.5,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar
                    sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        bgcolor: active ? "primary.main" : "action.selected",
                        color: active ? "primary.contrastText" : "text.secondary",
                    }}
                >
                    <Icon fontSize="small" />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h4" sx={{ mb: 0, fontWeight: 600 }}>{plugin.name}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">{plugin.category}</Typography>
                </Box>
                {plugin.installed && (
                    <Tooltip title="Acciones">
                        <IconButton size="small" aria-label="Acciones del plugin" disabled={busy} onClick={(event) => setMenuAnchor(event.currentTarget)} sx={{ flexShrink: 0 }}>
                            <MoreVertIcon color="energy_off" fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {plugin.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{plugin.description}</Typography>
            )}
            {!plugin.installed && !!plugin.requires?.length && (
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">Requiere:</Typography>
                    {plugin.requires.map((required) => (
                        <Typography key={required.slug} variant="caption" color={required.installed ? "success.main" : "warning.main"}
                                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                            {required.installed && <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                            {required.name}
                        </Typography>
                    ))}
                    {plugin.available && !!missing.length && (
                        <Typography variant="caption" color="text.secondary">· instálalo antes</Typography>
                    )}
                </Stack>
            )}

            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: "auto", pt: 2 }}>
                {footer()}
            </Stack>

            {plugin.installed && (
                <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                    {configurable && (
                        <MenuItem onClick={runAction(onConfigure)}>
                            <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Configurar</ListItemText>
                        </MenuItem>
                    )}
                    <MenuItem sx={{ color: "error.main" }} onClick={runAction(onUninstall)}>
                        <ListItemIcon sx={{ color: "inherit" }}><DeleteOutlineOutlinedIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Desinstalar</ListItemText>
                    </MenuItem>
                </Menu>
            )}
        </Card>
    );
};

export default PluginCard;
